import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import LineIcon from './LineIcon';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const BLUE      = '#3B82F6';
const FONT_BODY = "'DM Sans', sans-serif";

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'zip'];
const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.map(e => `.${e}`).join(',');

function AttachmentIcon({ type }) {
  return type?.startsWith('image/') ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
    </svg>
  );
}

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return i18n.t('messaging.today');
  if (date.toDateString() === yesterday.toDateString()) return i18n.t('messaging.yesterday');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function buildMessageRows(messages) {
  const rows = [];
  let lastDay = null;
  messages.forEach(msg => {
    const day = new Date(msg.created_at).toDateString();
    if (day !== lastDay) {
      rows.push({ type: 'sep', key: `sep-${day}`, label: formatDateLabel(msg.created_at) });
      lastDay = day;
    }
    rows.push({ type: 'msg', key: msg.id, msg });
  });
  return rows;
}

// Shared thread UI for support_conversations/support_messages -- used both by the admin
// Support tab and the account-side pinned "Vocation Works Support" panel. Deliberately
// separate from Messaging.jsx: that component's data model (conversations/messages tables,
// worker/employer/union participant columns, broadcast channels) doesn't map onto the
// simpler polymorphic support schema, so reworking it in place would risk regressing the
// production messaging feature it already serves.
export default function SupportThread({
  supabase, conversationId, viewerType, viewerId,
  title, subtitle, avatarLabel, avatarColor, onBack, headerRight,
}) {
  const { t } = useTranslation();
  const [messages, setMessages]     = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [text, setText]             = useState('');
  const [sending, setSending]       = useState(false);
  const [attachUploading, setAttachUploading] = useState(false);
  const [downloadingId, setDownloadingId]     = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef    = useRef(null);
  const attachInputRef = useRef(null);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    // Accounts have no UPDATE policy on support_messages (a row-level policy couldn't stop them
    // rewriting support's replies), so their side goes through mark_support_messages_read, which
    // only ever sets read_at on admin messages in a conversation the caller can access.
    const markIncomingRead = async (rows) => {
      const unreadIds = rows.filter(m => m.sender_type !== viewerType && !m.read_at).map(m => m.id);
      if (unreadIds.length === 0) return;
      if (viewerType === 'account') {
        await supabase.rpc('mark_support_messages_read', { p_conversation_id: conversationId });
      } else {
        await supabase.from('support_messages').update({ read_at: new Date().toISOString() }).in('id', unreadIds);
      }
    };

    const load = async () => {
      setLoadingMsgs(true);
      const { data } = await supabase
        .from('support_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      const rows = data || [];
      setMessages(rows);
      setLoadingMsgs(false);
      markIncomingRead(rows);
    };
    load();

    const channel = supabase
      .channel(`support-thread:${conversationId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversationId}` }, (payload) => {
        setMessages(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]);
        if (payload.new.sender_type !== viewerType) markIncomingRead([payload.new]);
      })
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [supabase, conversationId, viewerType]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    const content = text.trim();
    if (!content || sending || !conversationId) return;
    setSending(true);
    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const { data, error } = await supabase
      .from('support_messages')
      .insert({ conversation_id: conversationId, sender_type: viewerType, sender_id: viewerId, content })
      .select()
      .single();

    if (!error && data) {
      setMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
      supabase.from('support_conversations').update({ updated_at: new Date().toISOString() }).eq('id', conversationId).then(() => {});
    } else if (error) {
      setText(content);
    }
    setSending(false);
  };

  // ── Attach & send a file ────────────────────────────────────
  const handleAttachmentSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !conversationId || attachUploading) return;

    if (file.size > MAX_ATTACHMENT_BYTES) {
      alert(t('messaging.attachmentTooLarge'));
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
      alert(t('messaging.attachmentTypeNotSupported'));
      return;
    }

    setAttachUploading(true);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${conversationId}/${Date.now()}_${safeName}`;
    const { error: uploadError } = await supabase.storage
      .from('message-attachments')
      .upload(path, file, { contentType: file.type });
    if (uploadError) {
      setAttachUploading(false);
      alert(t('messaging.uploadFailedPrefix') + uploadError.message);
      return;
    }

    const { data, error } = await supabase
      .from('support_messages')
      .insert({
        conversation_id: conversationId, sender_type: viewerType, sender_id: viewerId,
        content: '', attachment_url: path, attachment_name: file.name, attachment_type: file.type,
      })
      .select()
      .single();
    if (error) {
      setAttachUploading(false);
      alert(t('messaging.couldNotSendAttachmentPrefix') + error.message);
      return;
    }
    setMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
    supabase.from('support_conversations').update({ updated_at: new Date().toISOString() }).eq('id', conversationId).then(() => {});
    setAttachUploading(false);
  };

  // ── Download an attachment (private bucket, so always via a fresh signed URL) ──
  const handleDownloadAttachment = async (msg) => {
    if (!msg.attachment_url) return;
    setDownloadingId(msg.id);
    const { data, error } = await supabase.storage
      .from('message-attachments')
      .createSignedUrl(msg.attachment_url, 60);
    setDownloadingId(null);
    if (error || !data?.signedUrl) {
      alert(t('messaging.couldNotOpenFilePrefix') + (error?.message || t('messaging.unknownErrorLower')));
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const panelHeader = {
    height: '52px', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px',
    backgroundColor: NAVY, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: FONT_BODY, backgroundColor: NAVY, overflow: 'hidden' }}>

      {/* Header */}
      <div style={panelHeader}>
        {onBack && (
          <button
            onClick={onBack}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: '4px 8px 4px 0', fontSize: '18px', lineHeight: 1, fontFamily: FONT_BODY }}
          >
            ←
          </button>
        )}
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          backgroundColor: avatarColor || BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '12px', fontWeight: 700, color: WHITE,
        }}>
          {avatarLabel || <LineIcon name="help" size={16} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '1px' }}>
              {subtitle}
            </div>
          )}
        </div>
        {headerRight}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column' }}>
        {loadingMsgs ? (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px', marginTop: '24px' }}>
            {t('messaging.loadingMessages')}
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px', marginTop: '24px' }}>
            {t('messaging.noMessagesYetLong')}
          </div>
        ) : (
          buildMessageRows(messages).map(item => {
            if (item.type === 'sep') {
              return (
                <div key={item.key} style={{ textAlign: 'center', margin: '16px 0 10px' }}>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '999px', padding: '3px 12px' }}>
                    {item.label}
                  </span>
                </div>
              );
            }
            const { msg } = item;
            const isMine = msg.sender_type === viewerType;
            const hasAttachment = !!msg.attachment_url;
            return (
              <div key={item.key} style={{
                display: 'flex', flexDirection: 'column',
                alignItems: isMine ? 'flex-end' : 'flex-start',
                alignSelf: isMine ? 'flex-end' : 'flex-start',
                marginBottom: '6px', maxWidth: '78%',
              }}>
                {hasAttachment ? (
                  <div style={{
                    padding: '10px 13px',
                    backgroundColor: isMine ? '#1A3A5C' : 'rgba(255,255,255,0.1)',
                    color: WHITE,
                    borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}>
                    <span style={{ color: AMBER, flexShrink: 0, display: 'flex' }}>
                      <AttachmentIcon type={msg.attachment_type} />
                    </span>
                    <span
                      title={msg.attachment_name}
                      style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}
                    >
                      {msg.attachment_name || t('messaging.attachmentFallback')}
                    </span>
                    <button
                      onClick={() => handleDownloadAttachment(msg)}
                      disabled={downloadingId === msg.id}
                      title={t('messaging.download')}
                      style={{
                        background: 'none', border: 'none', color: AMBER,
                        cursor: downloadingId === msg.id ? 'default' : 'pointer',
                        fontSize: '14px', fontWeight: 700, flexShrink: 0, padding: 0,
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      {downloadingId === msg.id ? '…' : (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                      )}
                    </button>
                  </div>
                ) : (
                  <div style={{
                    padding: '9px 13px',
                    backgroundColor: isMine ? '#1A3A5C' : 'rgba(255,255,255,0.1)',
                    color: WHITE,
                    borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    fontSize: '13.5px', lineHeight: 1.5, overflowWrap: 'break-word',
                  }}>
                    {msg.content}
                  </div>
                )}
                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.25)', marginTop: '2px' }}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'flex-end', gap: '10px',
        backgroundColor: NAVY, flexShrink: 0,
      }}>
        <input
          ref={attachInputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT}
          style={{ display: 'none' }}
          onChange={handleAttachmentSelected}
        />
        <button
          onClick={() => attachInputRef.current?.click()}
          disabled={attachUploading}
          title={t('messaging.attachAFile')}
          style={{
            width: '36px', height: '36px', borderRadius: '50%', border: 'none', flexShrink: 0,
            backgroundColor: 'rgba(255,255,255,0.08)',
            color: attachUploading ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
            cursor: attachUploading ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {attachUploading ? (
            <span style={{ fontSize: '10px' }}>…</span>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
          )}
        </button>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('messaging.typeAMessage')}
          rows={1}
          style={{
            flex: 1, resize: 'none',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '20px', padding: '9px 14px',
            backgroundColor: 'rgba(255,255,255,0.07)',
            color: WHITE, fontFamily: FONT_BODY, fontSize: '16px',
            lineHeight: 1.5, outline: 'none', overflowY: 'hidden',
          }}
        />
        <button
          onClick={handleSend}
          disabled={!text.trim() || sending}
          style={{
            width: '36px', height: '36px', borderRadius: '50%', border: 'none', flexShrink: 0,
            backgroundColor: text.trim() && !sending ? AMBER : 'rgba(255,255,255,0.12)',
            color: text.trim() && !sending ? NAVY : 'rgba(255,255,255,0.35)',
            cursor: text.trim() && !sending ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
